import { Button, Col, Divider, Drawer, EditButton, Icons, Image, Row, Show, Space, Tag, Typography } from "@pankod/refine-antd";
import { IResourceComponentsProps, useCan, useOne, useShow } from "@pankod/refine-core";
import { ManageCloudScreening } from "components/cloudscreening/manageCloudScreening";
import { RenderTimelineModal, StateButtons } from "components/common";
import { ShowUserCard } from "components/profile/showUserCard";
import { LookupButton } from "components/utils/lookupButton";
import { ObjectMetadata } from "components/utils/objectMetadata";
import dayjs from "dayjs";

import { IBaseProps, ICloudScreening, IScreeningConfigSet, IScreeningMetadata } from "interfaces";
import { arrayExtensions } from "lib-common";
import { useEffect, useState } from "react";
import { buildAttachmentUrl, DATAPROVIDER_ATTACHMENT, DATAPROVIDER_CONFIGSET, DATAPROVIDER_LOOKUP, DATAPROVIDER_READ, DATAPROVIDER_UPDATE, doesExist, RESOURCE_PATH, showDash } from "scripts/site";
const { Title, Text } = Typography;

export const CloudScreeningShow: React.FC<IResourceComponentsProps> = () => {
  const { queryResult } = useShow<ICloudScreening>({
    dataProviderName: DATAPROVIDER_READ,
  });
  const { data, isFetching } = queryResult;
  const record = data?.data as ICloudScreening;

  const { data: metadata } = useOne<IScreeningMetadata>({
    dataProviderName: DATAPROVIDER_LOOKUP,
    resource: RESOURCE_PATH.METADATA,
    id: RESOURCE_PATH.CLOUD_SCREENING
  });

  const { data: configSet } = useOne<IScreeningConfigSet>({
    dataProviderName: DATAPROVIDER_CONFIGSET,
    resource: RESOURCE_PATH.CLOUD_SCREENING,
    id: record?.id ?? "",
    queryOptions: {
      enabled: !!record?.id
    }
  });

  const metaConfig = metadata?.data?.config;
  // Set the rules for the request based on the config set. If config set is not found, then use the metadata config as default.
  const configSetRules = configSet?.data?.values ?? metaConfig?.defaultConfigSet;

  const canEdit = useCan({
    resource: RESOURCE_PATH.CLOUD_SCREENING,
    action: "edit",
    params: {
      dataProviderName: DATAPROVIDER_UPDATE,
      id: record?.id
    },
    queryOptions: {
      enabled: !!record?.id
    }
  });

  const twoColumnLayout = {
    xs: { span: 24 },
    sm: { span: 24 },
    md: { span: 16 },
    lg: { span: 10 },
    xl: { span: 10 },
    xxl: { span: 16 },
  };
  const rowGutter = { xs: 8, sm: 16, md: 24, lg: 32 };

  const [reviewFormOpen, switchReviewForm] = useState(false);
  const [refetchData, switchRefetchFlag] = useState(false);

  const refetchCloudScreeningData = () => {
    switchRefetchFlag(true);
  }

  const onFormSubmit = () => {
    switchReviewForm(false);
  };

  useEffect(() => {
    if (refetchData) {
      queryResult.refetch();
      switchRefetchFlag(false);
    }
  }, [refetchData, queryResult]);

  const currentStateMetadata = metadata?.data?.states?.find((state) => state.state === record?.stateManager?.state);

  const manageActions = metadata?.data?.states?.find(st =>
    st.state === record?.stateManager?.state
  )?.nextStates?.filter((st) => (configSetRules?.stateButtonsHideActions ?? []).includes(st?.action));

  const canAttachment = useCan({
    resource: RESOURCE_PATH.CLOUD_SCREENING,
    action: "get",
    params: {
      dataProviderName: DATAPROVIDER_ATTACHMENT,
      id: record?.id,
      filepath: record?.screeningDocument?.at(0)?.response?.url
    },
  });

  return (
    <Show
      isLoading={isFetching}
      headerProps={{
        title: record?.name,
        backIcon: true,
        tags: <Tag color={currentStateMetadata?.color ?? "default"}>
          {currentStateMetadata?.displayName ?? record?.stateManager?.state ?? "Loading"}
        </Tag>,
        extra: (!isFetching) && record &&
          <Space>
            {canEdit.data?.can && <>
              {configSetRules?.editableStates?.includes(record?.stateManager?.state) &&
                <EditButton></EditButton>
              }
              {(manageActions?.length ?? 0) > 0 && configSetRules?.canManageRequest &&
                <Button
                  type="primary"
                  key="manage"
                  onClick={() => { switchReviewForm(true) }}
                  icon={<Icons.FormOutlined />}>
                  {manageActions?.length === 1 ? manageActions?.at(0)?.buttonText : "Manage"}
                </Button>
              }
              <StateButtons {...record as IBaseProps}
                resourceToAct={RESOURCE_PATH.CLOUD_SCREENING}
                resourceToActId={record?.id || "0"}
                //resourceToNavigate={RESOURCE_PATH.CLOUD_SCREENING}
                hideActions={configSetRules?.stateButtonsHideActions}
                //navigateToId={record?.id}
                customPostStateAction={() => {
                  refetchCloudScreeningData();
                }}
              />
            </>}

            {record && arrayExtensions.validateArray(record?.screeningDocument)
              && canAttachment?.data?.can && configSetRules?.canAccessApprovalDoc === true &&
              <Button key="cndtresume"
                icon={<Icons.DownloadOutlined />}
                title="Download Attestation Document"
                type="primary"
                onClick={() => window.open(buildAttachmentUrl(RESOURCE_PATH.CLOUD_SCREENING, record?.id, record?.screeningDocument?.at(0)?.response?.url), "_blank")}>
                Attestation Document
              </Button>
            }
            <RenderTimelineModal stateManager={record?.stateManager} entityId={record?.id} entityName={RESOURCE_PATH.CLOUD_SCREENING} />
          </Space>
      }}
    >
      <Row gutter={rowGutter}>
        <Col className="gutter-row" {...twoColumnLayout}>
          <Title level={5}>Status</Title>
          <Tag color={currentStateMetadata?.color ?? "default"}>
            {currentStateMetadata?.displayName ?? record?.stateManager?.state ?? "Loading"}
          </Tag>
        </Col>
        <Col>
          <Title level={5}>Assigned To</Title>
          <Space wrap split={<Divider type="vertical" />} >
            {arrayExtensions.validateArray(record?.stateManager?.assignedTo ?? [])
              ? record?.stateManager?.assignedTo?.map((e, i) => <ShowUserCard key={`other-${i}`} id={e}></ShowUserCard>)
              : "None"}
          </Space>
        </Col>
      </Row>

      <Row gutter={rowGutter} style={{ marginTop: 15 }}>
        <Col className="gutter-row" {...twoColumnLayout}>
          <Title level={5}>TCS Employee Id</Title>
          <Text copyable={doesExist(record?.tcsEmployeeId)}>{showDash(record?.tcsEmployeeId)}</Text>
        </Col>
        <Col>
          <Title level={5}>Associate</Title>
          <Text copyable={doesExist(record?.associateId) && {
            text: record?.associateId,
          }}
          >{showDash(record?.name)}</Text>
        </Col>
      </Row>

      <Row gutter={rowGutter} style={{ marginTop: 15 }}>
        <Col className="gutter-row" {...twoColumnLayout}>
          <Title level={5}>TCS Manager</Title>
          <ShowUserCard id={record?.managerUserId} />
        </Col>
        <Col className="gutter-row" {...twoColumnLayout}>
          <Title level={5}>Supplier Id</Title>
          <Text copyable={doesExist(record?.supplierId)}>{showDash(record?.supplierId)}</Text>
        </Col>
      </Row>
      <Row gutter={rowGutter} style={{ marginTop: 15 }}>
        <Col className="gutter-row" {...twoColumnLayout}>
          <Title level={5}>Cloud Organization</Title>
          <Text>
            <LookupButton id={record?.cloudOrganizationId} resource={RESOURCE_PATH.CLOUD_ORGANIZATION} noClick></LookupButton>
          </Text>
        </Col>
        <Col className="gutter-row" {...twoColumnLayout}>
          <Title level={5}>Cloud Group</Title>
          <Text>
            <LookupButton id={record?.cloudOrganizationGroupId} resource={RESOURCE_PATH.CLOUD_ORGANIZATION_GROUP} noClick></LookupButton>
          </Text>
        </Col>
      </Row>
      <Row gutter={rowGutter} style={{ marginTop: 15 }}>
        <Col className="gutter-row" {...twoColumnLayout}>
          <Title level={5}>Screening Id</Title>
          <Text copyable={doesExist(record?.screeningId)}>{showDash(record?.screeningId)}</Text>
        </Col>
        <Col className="gutter-row" {...twoColumnLayout}>
          <Title level={5}>Screening Date</Title>
          <Text copyable={doesExist(record?.screeningDate)}>
            {record?.screeningDate ? dayjs(record.screeningDate).format("LL") : "---"}
          </Text>
        </Col>
      </Row>
      <Divider orientation="left">Location</Divider>
      <Row gutter={rowGutter} style={{ marginTop: 15 }}>
        <Col className="gutter-row" {...twoColumnLayout}>
          <Title level={5}>Physical Location</Title>
          <Text copyable={doesExist(record?.physicalLocationId) && {
            text: record?.physicalLocationId,
          }}>
            <LookupButton id={record?.physicalLocationId} resource={RESOURCE_PATH.COUNTRY} noClick></LookupButton>
          </Text>
        </Col>
        <Col className="gutter-row" {...twoColumnLayout}>
          <Title level={5}>Employee Central Location</Title>
          <LookupButton id={record?.employeeCentralLocationId} resource={RESOURCE_PATH.COUNTRY} noClick></LookupButton>
        </Col>
      </Row>

      <Row gutter={rowGutter} style={{ marginTop: 15 }}>
        <Col className="gutter-row">
          <Title level={5}>Screenshot of Employee Central Location</Title>
          {record?.screenshot?.length > 0 &&
            <Image
              style={{ maxWidth: "500px", maxHeight: "300px", borderRadius: 5 }}
              alt="Screenshot of Employee Central Location"
              src={buildAttachmentUrl(RESOURCE_PATH.CLOUD_SCREENING, record?.id, record?.screenshot?.at(0)?.response.url)}
            />
          }
        </Col>
      </Row>

      <Drawer
        title="Manage Request"
        placement="right"
        size="default"
        open={reviewFormOpen}
        onClose={() => { switchReviewForm(false) }}
      >
        {metadata?.data && record &&
          <ManageCloudScreening cloudScreeningRecord={record ?? null}
            cloudScreeningMetadata={metadata?.data} onFormSubmission={onFormSubmit}
            refetch={refetchCloudScreeningData}
            actions={manageActions}
          />
        }
      </Drawer>

      <Divider></Divider>
      <div style={{ marginTop: 15 }}>
        <ObjectMetadata {...(record as IBaseProps)}></ObjectMetadata>
      </div>
    </Show>
  );
};
